/**
 * Advanced search filters: module multi-select, entity type, date range.
 * Collapsible, hidden by default.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { MultiSelectFilter } from '../common/MultiSelectFilter';
import { SearchFilters } from '../../services/searchApi';
import { i18n } from '../../../i18n';
import { useModuleName } from '../../hooks/useModuleName';
import { useModule } from '../../contexts/ModuleContext';

interface GlobalSearchFiltersProps {
  filters: SearchFilters;
  onFiltersChange: (filters: SearchFilters) => void;
  isOpen: boolean;
  onToggle: () => void;
}

export function GlobalSearchFilters({
  filters,
  onFiltersChange,
  isOpen,
  onToggle,
}: GlobalSearchFiltersProps) {
  const { t } = useTranslation('common');
  const getModuleName = useModuleName();

  // The same modules the search itself covers — the ones this user can open.
  // Offering a module the results can never come from is a dead filter option.
  const { pickerModules, pinnedModules } = useModule();
  const availableModules = useMemo(
    () => [...pickerModules, ...pinnedModules],
    [pickerModules, pinnedModules],
  );

  const moduleOptions = useMemo(
    () =>
      availableModules.map((m) => ({
        value: m.id,
        label: i18n.exists(m.nameKey) ? getModuleName(m) : m.id,
      })),
    [availableModules, getModuleName],
  );

  // 'all' sentinel ↔ MultiSelectFilter's empty-means-all semantics.
  const selectedModules = filters.modules.includes('all') ? [] : filters.modules;

  const handleModulesChange = (values: string[]) => {
    const isAll = values.length === 0 || values.length === moduleOptions.length;
    onFiltersChange({
      ...filters,
      modules: isAll ? ['all'] : values,
      // Entity-type facets cascade to the selected modules; clear stale picks.
      entityTypes: [],
    });
  };

  const hasActiveFilters =
    !filters.modules.includes('all') ||
    filters.entityTypes.length > 0 ||
    filters.dateRange !== null;

  const clearFilters = () => {
    onFiltersChange({
      modules: ['all'],
      entityTypes: [],
      dateRange: null,
    });
  };

  return (
    <div className="border-b">
      <div className="flex items-center gap-2 px-3 py-1.5">
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          className="gap-1.5 h-7 text-xs"
        >
          <Filter className="h-3 w-3" />
          {t('globalSearch.filters')}
          {hasActiveFilters && (
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
          )}
        </Button>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="gap-1 h-7 text-xs text-muted-foreground"
          >
            <X className="h-3 w-3" />
            {t('globalSearch.clearFilters')}
          </Button>
        )}
      </div>

      {isOpen && (
        <div className="flex flex-wrap items-center gap-2 px-3 pb-2">
          {/* Module filter (multi-select) */}
          <MultiSelectFilter
            options={moduleOptions}
            selected={selectedModules}
            onChange={handleModulesChange}
            allLabel={t('globalSearch.allModules')}
            // Raw i18n key — MultiSelectFilter interpolates it as t(countLabel, { count }).
            // eslint-disable-next-line i18next/no-literal-string -- i18n key passed to t() inside MultiSelectFilter
            countLabel="globalSearch.modulesCount"
            // A search SCOPE filter: from "All Modules" the dominant intent is
            // narrowing to one module, so clicking one focuses it rather than
            // excluding it. Adding a second module is the next click.
            focusFromAll
            searchable
            width="w-full sm:w-[180px]"
            className="h-8 text-xs"
          />

          {/* Date range: from */}
          <Input
            type="date"
            className="w-[140px] h-8 text-xs"
            value={filters.dateRange?.from || ''}
            onChange={(e) => {
              onFiltersChange({
                ...filters,
                dateRange: {
                  from: e.target.value || undefined,
                  to: filters.dateRange?.to,
                },
              });
            }}
            placeholder={t('globalSearch.from', 'From')}
          />

          {/* Date range: to */}
          <Input
            type="date"
            className="w-[140px] h-8 text-xs"
            value={filters.dateRange?.to || ''}
            onChange={(e) => {
              onFiltersChange({
                ...filters,
                dateRange: {
                  from: filters.dateRange?.from,
                  to: e.target.value || undefined,
                },
              });
            }}
            placeholder={t('globalSearch.to', 'To')}
          />
        </div>
      )}
    </div>
  );
}
